/**
 * Firebase Loader - Ensures Firebase modules are properly loaded and registered
 * 
 * PROBLEM:
 * Firebase SDK v9+ uses a modular architecture where each service needs to register
 * its "component" with Firebase's internal provider system. Vite creates separate
 * chunks for each Firebase package, and they end up with SEPARATE internal state.
 * 
 * SOLUTION:
 * Use synchronous static imports for ALL Firebase modules. This ensures Vite bundles
 * them together with shared internal state. The trade-off is slightly larger initial
 * bundle, but it's necessary for Firebase to work correctly.
 */

// CRITICAL: Import ALL Firebase modules synchronously at the top level
// This ensures they share the same internal provider registry
import { 
  initializeApp, 
  getApp, 
  type FirebaseApp 
} from 'firebase/app';

import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  signOut as firebaseSignOut,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  type Auth
} from 'firebase/auth';

import {
  getFirestore,
  type Firestore
} from 'firebase/firestore';

import type { FirebaseStorage } from 'firebase/storage';
import type { Analytics } from 'firebase/analytics';
import type { Functions } from 'firebase/functions';
import type { Database } from 'firebase/database';

// Firebase configuration from environment variables
const firebaseConfig: Record<string, string | undefined> = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DB_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Only add measurementId if it's defined
if (import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) {
  firebaseConfig.measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;
}

export { firebaseConfig };

// Initialize Firebase App IMMEDIATELY on module load
// This MUST happen before any other Firebase operations
let app: FirebaseApp;
try {
  app = getApp();
  console.log('[Firebase Loader] Using existing Firebase app instance');
} catch {
  app = initializeApp(firebaseConfig);
  console.log('[Firebase Loader] Firebase app initialized');
}

export { app };

// Track initialization state
let _isInitialized = false;
let _initPromise: Promise<void> | null = null;

// Initialized service instances
let _auth: Auth | null = null;
let _firestore: Firestore | null = null;
let _storage: FirebaseStorage | null = null;
let _analytics: Analytics | null = null;
let _functions: Functions | null = null;
let _database: Database | null = null;

// Store module references for external access
interface FirebaseModules {
  auth: {
    onAuthStateChanged: typeof firebaseOnAuthStateChanged;
    signOut: typeof firebaseSignOut;
    signInWithCustomToken: typeof firebaseSignInWithCustomToken;
    getAuth: typeof getAuth;
    initializeAuth: typeof initializeAuth;
    indexedDBLocalPersistence: typeof indexedDBLocalPersistence;
    browserLocalPersistence: typeof browserLocalPersistence;
  } | null;
  firestore: typeof import('firebase/firestore') | null;
  storage: typeof import('firebase/storage') | null;
  functions: typeof import('firebase/functions') | null;
  database: typeof import('firebase/database') | null;
  analytics: typeof import('firebase/analytics') | null;
}

const modules: FirebaseModules = {
  auth: {
    onAuthStateChanged: firebaseOnAuthStateChanged,
    signOut: firebaseSignOut,
    signInWithCustomToken: firebaseSignInWithCustomToken,
    getAuth,
    initializeAuth,
    indexedDBLocalPersistence,
    browserLocalPersistence,
  },
  firestore: null,
  storage: null,
  functions: null,
  database: null,
  analytics: null,
};

/**
 * Initialize Firebase services.
 * Auth and Firestore are initialized synchronously (critical path).
 * Other services are loaded dynamically (optional).
 */
export async function initializeFirebase(): Promise<void> {
  if (_isInitialized) {
    return;
  }

  if (_initPromise) {
    return _initPromise;
  }

  _initPromise = (async () => {
    try {
      console.log('[Firebase Loader] Initializing Firebase services...');
      
      // STEP 1: Initialize Auth (synchronous - already imported)
      try {
        // First try getAuth - this works if auth is already initialized
        _auth = getAuth(app);
        console.log('[Firebase Loader] Auth retrieved via getAuth()');
      } catch (error: any) {
        if (error?.message?.includes('already been called')) {
          _auth = getAuth(app);
          console.log('[Firebase Loader] Auth already initialized');
        } else {
          console.log('[Firebase Loader] Initializing Auth with custom persistence...');
          _auth = initializeAuth(app, {
            persistence: [
              indexedDBLocalPersistence,
              browserLocalPersistence
            ],
            popupRedirectResolver: browserPopupRedirectResolver
          });
          console.log('[Firebase Loader] Auth initialized with persistence');
        }
      }

      // STEP 2: Initialize Firestore (synchronous - already imported)
      try {
        _firestore = getFirestore(app);
        console.log('[Firebase Loader] Firestore initialized');
      } catch (error) {
        console.warn('[Firebase Loader] Firestore initialization issue:', error);
      }

      // STEP 3: Load optional services dynamically (not critical path)
      try {
        const storageModule = await import('firebase/storage');
        modules.storage = storageModule;
        _storage = storageModule.getStorage(app);
        console.log('[Firebase Loader] Storage initialized');
      } catch (error) {
        console.warn('[Firebase Loader] Storage initialization skipped:', error);
      }

      try {
        const functionsModule = await import('firebase/functions');
        modules.functions = functionsModule;
        _functions = functionsModule.getFunctions(app, 'us-central1');
        console.log('[Firebase Loader] Functions initialized');
      } catch (error) {
        console.warn('[Firebase Loader] Functions initialization skipped:', error);
      }

      try {
        const databaseModule = await import('firebase/database');
        modules.database = databaseModule;
        _database = databaseModule.getDatabase(app);
        console.log('[Firebase Loader] Database initialized');
      } catch (error) {
        console.warn('[Firebase Loader] Database initialization skipped:', error);
      }

      // STEP 4: Initialize Analytics (browser-only)
      if (firebaseConfig.measurementId && typeof window !== 'undefined') {
        try {
          const analyticsModule = await import('firebase/analytics');
          modules.analytics = analyticsModule;
          _analytics = analyticsModule.initializeAnalytics(app, {
            config: {
              send_page_view: false
            }
          });
          console.log('[Firebase Loader] Analytics initialized');
        } catch (error) {
          console.warn('[Firebase Loader] Analytics initialization skipped:', error);
        }
      }

      _isInitialized = true;
      console.log('[Firebase Loader] Firebase initialization complete');

    } catch (error) {
      console.error('[Firebase Loader] Firebase initialization failed:', error);
      _initPromise = null;
      throw error;
    }
  })();

  return _initPromise;
}

/**
 * Get the Firebase app instance
 */
export function getFirebaseApp(): FirebaseApp {
  return app;
}

/**
 * Get the loaded Firebase modules (for use after initialization)
 */
export function getFirebaseModules(): FirebaseModules {
  return modules;
}

/**
 * Check if Firebase is initialized
 */
export function isFirebaseInitialized(): boolean {
  return _isInitialized;
}

/**
 * Wait for Firebase to be initialized (if not already)
 */
export async function waitForFirebase(): Promise<void> {
  if (_isInitialized) {
    return;
  }
  if (_initPromise) {
    return _initPromise;
  }
  return initializeFirebase();
}

/**
 * Get initialized service instances
 */
export function getAuthInstance(): Auth | null {
  return _auth;
}

export function getFirestoreInstance(): Firestore | null {
  return _firestore;
}

export function getStorageInstance(): FirebaseStorage | null {
  return _storage;
}

export function getFunctionsInstance(): Functions | null {
  return _functions;
}

export function getDatabaseInstance(): Database | null {
  return _database;
}

export function getAnalyticsInstance(): Analytics | null {
  return _analytics;
}

/**
 * Get auth functions from the loaded modules
 */
export async function getAuthFunctions() {
  await waitForFirebase();
  if (!modules.auth) {
    throw new Error('Auth module not loaded');
  }
  return modules.auth;
}

/**
 * Get firestore functions from the loaded modules
 */
export async function getFirestoreFunctions() {
  await waitForFirebase();
  if (!modules.firestore) {
    // Firestore is statically imported, so load it dynamically for the full module
    modules.firestore = await import('firebase/firestore');
  }
  return modules.firestore;
}
